import { fromUnixTime } from 'date-fns';
import type { ReactElement } from 'react';
import { useMemo } from 'react';

import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { Box } from '#/components/Box/Box';
import { Icon } from '#/components/Icon/Icon';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { Tag } from '#/components/Tag/Tag';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingLocationLabel } from '#/modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from '#/modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import type { ApiVolopPromotionType } from '#/modules/listing/volops/api/types/ApiVolopPromotionType';
import type { SearchAlgoliaVolopHit } from '#/modules/search/algolia/types/SearchAlgoliaVolopHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import {
  SearchHitInfoFooterHolder,
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
  SearchHitInfoPromotedLabel,
  SearchHitInfoStats,
} from '#/modules/search/components/Hit/Info/SearchHitInfo.styled';
import { SearchHitFloatingSaveButton } from '#/modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitApplicationStatus } from '#/modules/search/components/Hit/SearchHitApplicationStatus';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { formatSearchHitDate } from '#/modules/search/helpers/formatSearchHitDate';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';
import { distanceInWordsToNow } from '#/utils/date';
import {
  safeUtf8StringSlice,
  utf8StringLength,
} from '#/utils/string/safeUtf8String';

import { SearchVolopHitLabels } from './SearchVolopHitInfo.styled';

type Props = {
  hit: SearchAlgoliaVolopHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  actions: ReactElement | null;
  promotionType: ApiVolopPromotionType | null;
};

export function SearchVolopHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  highlighted,
  application,
  actions,
  promotionType,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone: hit.remoteZone,
          remoteCountry: hit.remoteCountry || hit.country,
          remoteState: hit.remoteState || hit.state,
        },
        address: {
          city: hit.city,
          country: hit.country,
          stateCode: hit.state,
        },
      }),
    [i18n, hit],
  );

  const showPostedDate =
    hit.published && promotionType !== 'PROMOTED_VOLOP_RADIUS_NWIDE';

  const startDateTime = hit.startDate && hit.startTime ? hit.startsLocal : null;
  const endDateTime = hit.endDate && hit.endTime ? hit.endsLocal : null;

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile={!hit.logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={hit.image}
              source={hit.source}
              name={hit.name}
              logo={hit.logo}
            />
          </SearchHitImageHolder>

          <SearchHitInfoHolder>
            {showLabel && (
              <div style={{ marginBottom: 14 }}>
                <ListingTypeLabel listingType="VOLOP" orgType={null} />
              </div>
            )}

            <SearchHitInfoHeader>
              <SearchHitInfoHeaderHeading>
                <div>
                  <span data-qa-id="search-result-link">
                    {safeUtf8StringSlice(hit.name, 0, 120)}
                  </span>
                  {utf8StringLength(hit.name) > 120 && <>&hellip;</>}
                </div>

                {hit.isPostedAnonymously && (
                  <Box display="inline-block" ml="4px" verticalAlign="middle">
                    <LegacyTooltip width={360}>
                      {i18n(
                        `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                      )}
                    </LegacyTooltip>
                  </Box>
                )}
              </SearchHitInfoHeaderHeading>

              {(hit.orgType === 'CORPORATION' || hit.orgName) && (
                <SearchHitInfoHeaderOrgName>
                  <Box display="flex" alignItems="center" flexWrap="wrap">
                    {hit.orgName}
                  </Box>
                </SearchHitInfoHeaderOrgName>
              )}
            </SearchHitInfoHeader>

            <SearchVolopHitLabels>
              {hit.locationType && (
                <ListingLocationTypeLabel
                  listingType="VOLOP"
                  locationType={hit.locationType}
                />
              )}

              {promotionType !== 'PROMOTED_VOLOP_RADIUS_NWIDE' &&
                hit.locationType && (
                  <ListingLocationLabel
                    locationType={hit.locationType}
                    location={location}
                  />
                )}

              {(startDateTime ?? endDateTime) && (
                <Tag size="micro" prefix={<Icon name="calendar" size={14} />}>
                  {formatSearchHitDate({
                    locale,
                    startDateTime,
                    endDateTime,
                    timezone: hit.timezone,
                  })}
                </Tag>
              )}

              {hit.detailsStipendProvided && (
                <Tag size="micro">{i18n('Stipend Provided')}</Tag>
              )}

              {hit.detailsAcademicCreditAvailable && (
                <Tag size="micro">{i18n('Academic Credit')}</Tag>
              )}

              {hit.detailsTrainingProvided && (
                <Tag size="micro">{i18n('Training Provided')}</Tag>
              )}

              {hit.canBeDoneInADay && (
                <Tag size="micro">{i18n('Done in a Day')}</Tag>
              )}

              {hit.welcomeFamilies && (
                <Tag size="micro">{i18n('Family Friendly')}</Tag>
              )}

              {hit.detailsHousingAvailable && (
                <Tag size="micro">{i18n('Housing Available')}</Tag>
              )}

              {hit.welcomeGroups && (
                <Tag size="micro">{i18n('Good for Groups')}</Tag>
              )}
            </SearchVolopHitLabels>

            <SearchHitInfoFooterHolder>
              {showPostedDate && (
                <SearchHitInfoStats>
                  <BodyMicro lineHeight={1}>
                    {i18n('Posted %(postedAt)s', {
                      postedAt: distanceInWordsToNow({
                        date: fromUnixTime(hit.published),
                        locale,
                        dtOverride: appConfig.idealist.dtOverride,
                      }),
                    })}
                  </BodyMicro>
                </SearchHitInfoStats>
              )}

              {promotionType && (
                <SearchHitInfoPromotedLabel>
                  {i18n('FEATURED')}
                </SearchHitInfoPromotedLabel>
              )}

              {application && (
                <SearchHitApplicationStatus
                  status={application.status}
                  created={application.created}
                />
              )}
            </SearchHitInfoFooterHolder>
          </SearchHitInfoHolder>
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
